import { Injectable } from '@angular/core';
import { ChallengeActivityQuery } from '@examdojo/challenge';
import { Icon } from '@examdojo/core/icon';
import { HomeworkActivityQuery } from '@examdojo/homework';
import { PracticeActivityQuery } from '@examdojo/practice-activity';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';
import { FEATURE_TO_ICON } from '../features/features.model';
import { ACTIVITY_TYPE_TO_FEATURE, ActivityType } from '../shared/activity-conflict/activity-conflict.utils';

export interface OngoingActivity {
  type: ActivityType;
  id: string;
  route: string;
  icon: Icon;
  nameKey: string;
  syllabusId: number | null;
  courseLevel: string | null;
}

@Injectable({ providedIn: 'root' })
export class ActivityQuery {
  constructor(
    private readonly practiceActivityQuery: PracticeActivityQuery,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly homeworkActivityQuery: HomeworkActivityQuery,
  ) {}

  readonly ongoingActivity$: Observable<OngoingActivity | null> = combineLatest([
    this.practiceActivityQuery.ongoingActivity$,
    this.challengeActivityQuery.ongoingActivity$,
    this.homeworkActivityQuery.ongoingActivity$,
  ]).pipe(
    map(([practice, challenge, homework]) => {
      if (challenge) {
        const feature = ACTIVITY_TYPE_TO_FEATURE['challenge'];
        return {
          type: 'challenge' as ActivityType,
          id: challenge.id,
          route: `/${feature}`,
          icon: FEATURE_TO_ICON[feature],
          nameKey: 'examdojo.activity.mock_exam',
          syllabusId: challenge.syllabus_id,
          courseLevel: challenge.course_level,
        };
      }

      if (homework) {
        const feature = ACTIVITY_TYPE_TO_FEATURE['homework'];
        return {
          type: 'homework' as ActivityType,
          id: homework.id,
          route: `/${feature}`,
          icon: FEATURE_TO_ICON[feature],
          nameKey: 'examdojo.activity.homework',
          syllabusId: homework.syllabus_id,
          courseLevel: homework.course_level,
        };
      }

      if (practice) {
        const feature = ACTIVITY_TYPE_TO_FEATURE['practice'];
        return {
          type: 'practice' as ActivityType,
          id: practice.id,
          route: `/${feature}`,
          icon: FEATURE_TO_ICON[feature],
          nameKey: 'examdojo.activity.practice',
          syllabusId: practice.syllabus_id,
          courseLevel: practice.course_level,
        };
      }

      return null;
    }),
    distinctUntilChanged((a, b) => a?.id === b?.id && a?.type === b?.type),
  );

  readonly hasOngoingActivity$: Observable<boolean> = this.ongoingActivity$.pipe(map((activity) => activity !== null));
}
